import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../lib/trpc";
import { Icon } from "../components/ui/Icon";
import { Drawer } from "../components/Drawer";
import { DateField } from "../components/ui/DateField";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { useIsMobile } from "../lib/useIsMobile";
import { MCard, KV } from "../components/mobile/primitives";
import { hizliAralik, HIZLI_ARALIKLAR, todayISO, type HizliAralik } from "../lib/trDate";
import "./CompanyAccount.css";

/**
 * CARİ HESAP EKSTRESİ — muhasebe defteri düzeninde tek sayfa (uygulama içi sekme).
 *
 * ── NEDEN SEKME ──
 * Ekstre uzun bir belgedir; kullanıcı bir sevkiyata ya da siparişe bakıp geri döndüğünde
 * filtreleri ve kaydırma konumu KAYBOLMAMALI. Çekmece kapanırdı, sekme mount kalır.
 *
 * ── PARA BİRİMİ (kullanıcı kuralı: "anlaşma dolar ise hesap dolar üzerinden yürür") ──
 * Bakiye para birimi BAZINDA gösterilir; farklı para birimleri TOPLANMAZ. TL karşılığı
 * yalnız en altta, "≈" ile ve bilgi amaçlı. Kur her gün değiştiği için dövizli bakiyeyi
 * TL'ye çevirip tek rakam sunmak, ödeme günü tutmayan sahte bir bakiye üretirdi.
 */

const PARA: Record<string, string> = { TRY: "₺", USD: "$", EUR: "€", GBP: "£" };

const TUR_ETIKET: Record<string, string> = {
  opening: "Devir",
  sale: "Satış / Sevkiyat",
  collection: "Tahsilat",
  purchase: "Alım",
  supplier_payment: "Tedarikçi Ödemesi",
  freight: "Nakliye",
  adjustment: "Düzeltme",
};
const YONTEM_ETIKET: Record<string, string> = {
  cash: "Nakit", transfer: "Havale/EFT", check: "Çek",
  promissory: "Senet", credit_card: "Kredi Kartı", offset: "Mahsup",
};

/** Para: 1.234.567,89 — mono + tabular (muhasebe sütunu hizalı okunmalı). */
function fmtPara(n: number): string {
  return new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}
function fmtTarih(d: string | Date): string {
  return new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(d));
}

export function CompanyAccountPage() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const isMobile = useIsMobile();

  // Dönem KPI'ları YEREL takvimden hesaplanır ve sunucuya gönderilir (sunucu saat dilimi
  // TR'den farklıysa "bu ay" sınırı kayardı — trDate.ts başındaki UTC tuzağının aynısı).
  const periods = useMemo(
    () => HIZLI_ARALIKLAR.map((h) => ({ key: h.key, ...hizliAralik(h.key) })),
    [],
  );

  const [aralik, setAralik] = useState<HizliAralik | "tumu" | "ozel">("tumu");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [paraSuzgec, setParaSuzgec] = useState<"" | "TRY" | "USD" | "EUR" | "GBP">("");

  const ozetQ = trpc.account.summary.useQuery({ companyId: id, periods }, { enabled: !!id });
  const ekstreQ = trpc.account.statement.useQuery(
    { companyId: id, from: from || undefined, to: to || undefined, currency: paraSuzgec || undefined },
    { enabled: !!id, placeholderData: (p) => p },
  );

  const [odemeAcik, setOdemeAcik] = useState(false);
  const [silTarget, setSilTarget] = useState<{ id: string; aciklama: string } | null>(null);
  const silMut = trpc.account.removeTransaction.useMutation();

  function hizliSec(key: HizliAralik | "tumu") {
    setAralik(key);
    if (key === "tumu") { setFrom(""); setTo(""); return; }
    const r = hizliAralik(key);
    setFrom(r.from); setTo(r.to);
  }

  const ozet = ozetQ.data;
  const satirlar = ekstreQ.data?.rows ?? [];
  const devir = ekstreQ.data?.opening ?? {};

  // Ekstrede geçen para birimleri — sütun başlıkları ve alt toplam için.
  const paralar = useMemo(() => {
    const s = new Set<string>();
    for (const b of ozet?.balances ?? []) s.add(b.currency);
    return [...s];
  }, [ozet]);

  const tlKarsiligi = useMemo(() => {
    let toplam = 0, eksik = false;
    for (const b of ozet?.balances ?? []) { toplam += b.tryEquivalent; if (b.tryEquivalentEksik) eksik = true; }
    return { toplam, eksik };
  }, [ozet]);

  if (!id) return null;

  return (
    /* `items-page` = UYGULAMANIN STANDART SAYFA KABI (22/28/48 padding + 1760 max-width +
       ortalama). Cari hesap sayfası kendi köküyle başladığı için bu kabı almıyordu ve
       kenar boşlukları diğer sayfalardan FARKLI görünüyordu. Sınıf devralınır; `.cacc`
       yalnız dikey akışı (flex + gap) ekler — özellikler çakışmaz.
       Mobilde `.m-app .items-page { padding: 0 }` zaten sıfırlar, kabuk kendi boşluğunu verir. */
    <div className="items-page cacc">
      {/* ═══ KÜNYE — belge başlığı (kim, hangi roller, iletişim) ═══ */}
      <div className="cacc-head">
        <div className="cacc-head-main">
          <div className="cacc-head-top">
            <Link to="/companies" className="cacc-back"><Icon name="chevLeft" size={14} />Cariler</Link>
            {ozet?.company.isActive === false ? <span className="badge-pill">Pasif</span> : null}
          </div>
          <h1 className="cacc-name">{ozet?.company.name ?? "…"}</h1>
          <div className="cacc-meta">
            <span className="cell-id">{ozet?.company.code ?? "—"}</span>
            {(ozet?.company.roles ?? []).map((r) => (
              <span key={r} className={`badge-pill ${r === "customer" ? "b-info" : "b-warn"}`}>
                {r === "customer" ? "Müşteri" : "Tedarikçi"}
              </span>
            ))}
          </div>
          <div className="cacc-kv">
            {ozet?.company.taxNumber ? <span><i>Vergi No</i><b className="mono">{ozet.company.taxNumber}</b></span> : null}
            {ozet?.company.phone ? <span><i>Telefon</i><b className="mono">{ozet.company.phone}</b></span> : null}
            {ozet?.company.email ? <span><i>E-posta</i><b>{ozet.company.email}</b></span> : null}
            {ozet?.company.city ? <span><i>Şehir</i><b>{ozet.company.city}{ozet.company.country ? ` · ${ozet.company.country}` : ""}</b></span> : null}
            {ozet?.firstTxDate ? <span><i>İlk hareket</i><b className="mono">{fmtTarih(ozet.firstTxDate)}</b></span> : null}
            {ozet?.lastTxDate ? <span><i>Son hareket</i><b className="mono">{fmtTarih(ozet.lastTxDate)}</b></span> : null}
          </div>
        </div>

        {/* ═══ BAKİYE — para birimi BAZINDA (toplanmaz) ═══ */}
        <div className="cacc-bal">
          {(ozet?.balances ?? []).length === 0 ? (
            <div className="cacc-bal-empty"><Icon name="banknote" size={18} />Henüz hesap hareketi yok</div>
          ) : (
            <>
              {(ozet?.balances ?? []).map((b) => (
                <div key={b.currency} className={`cacc-bal-card ${b.balance > 0.005 ? "borclu" : b.balance < -0.005 ? "alacakli" : "kapali"}`}>
                  <span className="k">{b.currency} BAKİYE</span>
                  <span className="v mono">{PARA[b.currency] ?? ""}{fmtPara(Math.abs(b.balance))}</span>
                  {/* Durum = renk + İKON + METİN (renk tek başına yetmez, CLAUDE.md ✅#5) */}
                  <span className="d">
                    <Icon name={b.balance > 0.005 ? "arrowLeft" : b.balance < -0.005 ? "arrowRight" : "checkCircle"} size={11} />
                    {b.balance > 0.005 ? "Cari BORÇLU — bizim alacağımız"
                      : b.balance < -0.005 ? "BİZ BORÇLUYUZ — cariye ödenecek"
                      : "Hesap kapalı"}
                  </span>
                  <span className="s mono">B {fmtPara(b.debit)} · A {fmtPara(b.credit)}</span>
                </div>
              ))}
              {paralar.length > 1 ? (
                <div className="cacc-bal-try">
                  ≈ {fmtPara(tlKarsiligi.toplam)} ₺ toplam karşılık
                  {tlKarsiligi.eksik ? <b> · kur girilmemiş hareket var</b> : null}
                  <i>belge tarihindeki kurlarla, bilgi amaçlı</i>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>

      {/* ═══ TİCARET HACMİ — dönem KPI'ları ═══ */}
      <div className="cacc-kpis">
        {HIZLI_ARALIKLAR.map((h) => {
          const p = ozet?.periods.find((x) => x.key === h.key);
          const bos = !p || p.byCurrency.length === 0;
          return (
            <div key={h.key} className="cacc-kpi">
              <span className="t">{h.label}</span>
              {bos ? (
                <span className="bos">Hareket yok</span>
              ) : (
                p!.byCurrency.map((c) => (
                  <div key={c.currency} className="cacc-kpi-row">
                    {/* Satış 0 iken rakam SOLUK: o dönem yalnız tahsilat olmuş demektir.
                        Koyu "0,00" ile dolu tahsilat yan yana durunca hangisinin hangisi
                        olduğu okunmuyordu. */}
                    <span className={`mono cacc-kpi-sale${c.sales <= 0.005 ? " sifir" : ""}`}>
                      {PARA[c.currency] ?? ""}{fmtPara(c.sales)}
                    </span>
                    {c.collections > 0.005 ? (
                      <span className="mono cacc-kpi-col" title="Bu dönem tahsil edilen">
                        <Icon name="arrowRight" size={10} />{PARA[c.currency] ?? ""}{fmtPara(c.collections)}
                      </span>
                    ) : null}
                  </div>
                ))
              )}
              <span className="h">satış · tahsilat</span>
            </div>
          );
        })}
      </div>

      {/* ═══ EKSTRE ═══ */}
      <div className="d-card cacc-card">
        <div className="d-card-head">
          <div className="left">
            <span className="t">Hesap Ekstresi</span>
            <span className="s">{satirlar.length} hareket</span>
          </div>
          <button type="button" className="btn-md primary" onClick={() => setOdemeAcik(true)}>
            <Icon name="plus" size={14} />Hareket / Tahsilat Ekle
          </button>
        </div>

        <div className="cacc-filters">
          <div className="cacc-quick">
            <button type="button" className={`cacc-chip${aralik === "tumu" ? " act" : ""}`} onClick={() => hizliSec("tumu")}>Tümü</button>
            {HIZLI_ARALIKLAR.map((h) => (
              <button key={h.key} type="button" className={`cacc-chip${aralik === h.key ? " act" : ""}`} onClick={() => hizliSec(h.key)}>{h.label}</button>
            ))}
          </div>
          <div className="cacc-range">
            <DateField value={from} onChange={(v) => { setFrom(v); setAralik("ozel"); }} aria-label="Başlangıç tarihi" placeholder="Başlangıç" />
            <span className="cacc-tire">–</span>
            <DateField value={to} onChange={(v) => { setTo(v); setAralik("ozel"); }} aria-label="Bitiş tarihi" placeholder="Bitiş" />
            {paralar.length > 1 ? (
              <select className="form-select cacc-cursel" value={paraSuzgec} onChange={(e) => setParaSuzgec(e.target.value as typeof paraSuzgec)} aria-label="Para birimi süzgeci">
                <option value="">Tüm para birimleri</option>
                {paralar.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            ) : null}
          </div>
        </div>

        <div className="d-card-body flush">
          {ekstreQ.isLoading ? (
            <div className="cacc-empty">Yükleniyor…</div>
          ) : satirlar.length === 0 && Object.keys(devir).length === 0 ? (
            <div className="cacc-empty">
              <Icon name="banknote" size={26} />
              <span>Bu aralıkta hesap hareketi yok.</span>
              <span className="alt">Sevkiyat onaylandıkça satış borcu buraya otomatik işlenir; tahsilatı yukarıdaki butondan girin.</span>
            </div>
          ) : isMobile ? (
            <div className="cacc-mlist">
              {satirlar.map((r) => (
                <MCard pad key={r.id}>
                  <div className="cacc-mtop">
                    <span className="mono">{fmtTarih(r.txDate)}</span>
                    <span className="badge-pill">{TUR_ETIKET[r.type] ?? r.type}</span>
                  </div>
                  <KV k="Açıklama" v={r.description} />
                  {r.documentNo ? <KV k="Belge" v={r.documentNo} mono /> : null}
                  <KV k={r.debit > 0 ? "Borç" : "Alacak"} v={`${PARA[r.currency] ?? ""}${fmtPara(r.debit > 0 ? r.debit : r.credit)}`} mono />
                  <KV k="Bakiye" v={`${PARA[r.currency] ?? ""}${fmtPara(r.runningBalance)}`} mono />
                </MCard>
              ))}
            </div>
          ) : (
            <div className="dt2-wrap">
              <table className="dt2 cacc-table">
                <thead>
                  <tr>
                    {/* Genişlikler `table-layout: fixed` ile BAĞLAYICI: toplam sabit 686px,
                        kalan yer tek esnek sütuna (AÇIKLAMA) gider. Para sütunları 7 haneli
                        tutar + kuruşu tek satırda taşır; böylece hiçbir ekran genişliğinde
                        tutar kırpılmaz. */}
                    <th style={{ width: 86 }}>TARİH</th>
                    <th style={{ width: 98 }}>TÜR</th>
                    <th>AÇIKLAMA</th>
                    <th style={{ width: 130 }}>BELGE NO</th>
                    <th className="num" style={{ width: 106 }}>BORÇ</th>
                    <th className="num" style={{ width: 106 }}>ALACAK</th>
                    <th className="num" style={{ width: 120 }}>BAKİYE</th>
                    <th style={{ width: 40 }} aria-label="İşlem"></th>
                  </tr>
                </thead>
                <tbody>
                  {/* DEVİR — aralık başındaki bakiye. Bu satır olmadan ekstre sıfırdan
                      başlıyormuş gibi görünür ve yürüyen bakiye yanlış okunur. */}
                  {Object.entries(devir).map(([cur, tutar]) => (
                    <tr key={`devir-${cur}`} className="cacc-devir">
                      <td className="mono">{from ? fmtTarih(from) : "—"}</td>
                      <td>Devir</td>
                      <td className="cacc-desc">Önceki dönemden devreden bakiye ({cur})</td>
                      <td>—</td>
                      <td className="cell-num mono">{tutar > 0 ? fmtPara(tutar) : ""}</td>
                      <td className="cell-num mono">{tutar < 0 ? fmtPara(-tutar) : ""}</td>
                      <td className="cell-num mono">{PARA[cur] ?? ""}{fmtPara(tutar)}</td>
                      <td></td>
                    </tr>
                  ))}
                  {satirlar.map((r) => (
                    <tr key={r.id} className={r.type === "collection" ? "cacc-tahsilat" : undefined}>
                      <td className="mono">{fmtTarih(r.txDate)}</td>
                      <td>
                        <span className="cacc-tur">{TUR_ETIKET[r.type] ?? r.type}</span>
                        {r.method ? <span className="cacc-yontem">{YONTEM_ETIKET[r.method] ?? r.method}</span> : null}
                      </td>
                      <td className="cacc-desc">
                        {r.shipmentId ? <Link to="/shipments" className="cacc-link">{r.description}</Link> : r.description}
                        {r.reference ? <span className="cacc-ref">{r.reference}</span> : null}
                      </td>
                      {/* `title`: sütun dar kalırsa belge no kırpılır — tam değer ERİŞİLEBİLİR kalmalı,
                          muhasebede belge numarası kaybolmaz. */}
                      <td className="mono cacc-belge" title={r.documentNo ?? undefined}>{r.documentNo ?? "—"}</td>
                      <td className="cell-num mono cacc-borc">{r.debit > 0.005 ? fmtPara(r.debit) : ""}</td>
                      <td className="cell-num mono cacc-alacak">{r.credit > 0.005 ? fmtPara(r.credit) : ""}</td>
                      <td className="cell-num mono cacc-bakiye">{PARA[r.currency] ?? ""}{fmtPara(r.runningBalance)}</td>
                      <td>
                        {r.editable ? (
                          <button type="button" className="iact danger" aria-label="Hareketi sil" title="Hareketi sil"
                            onClick={() => { silMut.reset(); setSilTarget({ id: r.id, aciklama: r.description }); }}>
                            <Icon name="trash" size={14} />
                          </button>
                        ) : (
                          // Belgeye bağlı satır: silme YOK. Sebebi görünür olsun (sessiz eksik buton değil).
                          <span className="cacc-kilit" title="Sevkiyattan doğan borç — sevkiyat geri alınarak kalkar">
                            <Icon name="lock" size={12} />
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {/* ALT TOPLAM — para birimi bazında, muhasebe defteri kapanışı gibi. */}
                <tfoot>
                  {(ozet?.balances ?? []).map((b) => (
                    <tr key={`t-${b.currency}`}>
                      <td colSpan={4} className="cacc-tfoot-lbl">{b.currency} TOPLAM</td>
                      <td className="cell-num mono">{fmtPara(b.debit)}</td>
                      <td className="cell-num mono">{fmtPara(b.credit)}</td>
                      <td className="cell-num mono cacc-tfoot-bal">{PARA[b.currency] ?? ""}{fmtPara(b.balance)}</td>
                      <td></td>
                    </tr>
                  ))}
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>

      <HareketDrawer
        open={odemeAcik}
        companyId={id}
        roles={ozet?.company.roles ?? []}
        onClose={() => setOdemeAcik(false)}
        onSaved={() => { setOdemeAcik(false); void qc.invalidateQueries(); }}
      />

      {silTarget ? (
        <ConfirmDialog
          open
          title="Hareketi sil"
          tone="danger"
          confirmLabel="Sil"
          busy={silMut.isPending}
          message={
            <>
              <p style={{ marginTop: 0 }}><b>{silTarget.aciklama}</b> hareketi silinecek ve bakiye buna göre değişecek.</p>
              {silMut.error ? <div className="form-banner-err" role="alert">{silMut.error.message}</div> : null}
            </>
          }
          onConfirm={() => {
            void (async () => {
              try {
                await silMut.mutateAsync({ id: silTarget.id });
                await qc.invalidateQueries();
                setSilTarget(null);
              } catch { /* hata dialogda görünür */ }
            })();
          }}
          onCancel={() => setSilTarget(null)}
        />
      ) : null}
    </div>
  );
}

/** Tahsilat / ödeme / manuel hareket girişi. */
function HareketDrawer({ open, companyId, roles, onClose, onSaved }: {
  open: boolean; companyId: string; roles: string[]; onClose: () => void; onSaved: () => void;
}) {
  const musteriMi = roles.includes("customer");
  const [tur, setTur] = useState<"collection" | "supplier_payment" | "opening" | "adjustment" | "freight">(
    musteriMi ? "collection" : "supplier_payment",
  );
  /**
   * Varsayılan tür çekmece AÇILDIĞINDA rollere göre TAZELENİR.
   *
   * `useState` başlangıcı yalnız İLK render'da okunur; o anda özet sorgusu henüz dönmemiş
   * olduğu için `roles` boştu ve müşteri carisinde varsayılan "tedarikçiye ödendi" kalıyordu.
   * Dikkatsiz bir kayıtta tahsilat ALACAK yerine BORÇ yazılır ve bakiye ters yönde bozulurdu —
   * muhasebede sessiz ve pahalı bir hata.
   */
  useEffect(() => {
    if (!open) return;
    setTur(roles.includes("customer") ? "collection" : "supplier_payment");
  }, [open, roles]);
  const [yon, setYon] = useState<"debit" | "credit">("credit");
  const [tutar, setTutar] = useState("");
  const [para, setPara] = useState<"TRY" | "USD" | "EUR" | "GBP">("TRY");
  const [kur, setKur] = useState("");
  const [tarih, setTarih] = useState(todayISO());
  const [yontem, setYontem] = useState<"cash" | "transfer" | "check" | "promissory" | "credit_card" | "offset">("transfer");
  const [aciklama, setAciklama] = useState("");
  const [belgeNo, setBelgeNo] = useState("");
  const [referans, setReferans] = useState("");
  const [hata, setHata] = useState<string | null>(null);
  const mut = trpc.account.addTransaction.useMutation();

  const yonSecilir = tur === "opening" || tur === "adjustment";
  const odemeMi = tur === "collection" || tur === "supplier_payment";

  async function kaydet() {
    setHata(null);
    const t = Number(tutar.replace(/\./g, "").replace(",", "."));
    if (!Number.isFinite(t) || t <= 0) { setHata("Tutar sıfırdan büyük olmalı."); return; }
    if (!aciklama.trim()) { setHata("Açıklama zorunlu."); return; }
    const k = kur.trim() ? Number(kur.replace(",", ".")) : null;
    if (para !== "TRY" && (!k || k <= 0)) { setHata(`${para} hareketinde kur zorunlu (1 ${para} = ? TRY).`); return; }
    try {
      await mut.mutateAsync({
        companyId, type: tur, ...(yonSecilir ? { direction: yon } : {}),
        amount: t, currency: para, exchangeRate: para === "TRY" ? null : k,
        txDate: tarih, method: odemeMi ? yontem : null,
        reference: referans.trim() || null, description: aciklama.trim(), documentNo: belgeNo.trim() || null,
      });
      setTutar(""); setAciklama(""); setBelgeNo(""); setReferans("");
      onSaved();
    } catch (e) { setHata((e as Error).message); }
  }

  return (
    <Drawer open={open} onClose={onClose} title="Hesap Hareketi" subtitle="Tahsilat, ödeme ya da manuel düzeltme" size="md"
      footer={
        <div className="drawer-foot">
          <button type="button" className="btn-md ghost" onClick={onClose}>Vazgeç</button>
          <button type="button" className="btn-md primary" disabled={mut.isPending} onClick={() => void kaydet()}>
            <Icon name="check" size={14} />{mut.isPending ? "Kaydediliyor…" : "Kaydet"}
          </button>
        </div>
      }>
      {hata ? <div className="form-banner-err" role="alert">{hata}</div> : null}

      <div className="form-field">
        <label className="lbl" htmlFor="hrk-tur">İşlem Türü <span className="req">*</span></label>
        <select id="hrk-tur" className="form-select" value={tur} onChange={(e) => setTur(e.target.value as typeof tur)}>
          <option value="collection">Tahsilat — müşteriden ödeme alındı</option>
          <option value="supplier_payment">Ödeme — tedarikçiye ödendi</option>
          <option value="freight">Nakliye gideri — cariye yansıtıldı</option>
          <option value="opening">Açılış / Devir bakiyesi</option>
          <option value="adjustment">Düzeltme (iade, iskonto, kur farkı…)</option>
        </select>
        <span className="help">
          {tur === "collection" ? "Carinin bize olan borcunu AZALTIR (alacak)."
            : tur === "supplier_payment" ? "Bizim cariye olan borcumuzu AZALTIR (borç)."
            : tur === "freight" ? "Cariye BORÇ yazılır."
            : "Yönü aşağıdan seçin."}
        </span>
      </div>

      {yonSecilir ? (
        <div className="form-field">
          <label className="lbl" htmlFor="hrk-yon">Yön <span className="req">*</span></label>
          <select id="hrk-yon" className="form-select" value={yon} onChange={(e) => setYon(e.target.value as typeof yon)}>
            <option value="debit">Borç — cari bize borçlanır</option>
            <option value="credit">Alacak — cari alacaklanır</option>
          </select>
        </div>
      ) : null}

      <div className="cacc-drow">
        <div className="form-field">
          <label className="lbl" htmlFor="hrk-tutar">Tutar <span className="req">*</span></label>
          <input id="hrk-tutar" className="form-input mono" inputMode="decimal" value={tutar}
            onChange={(e) => setTutar(e.target.value)} placeholder="0,00" />
        </div>
        <div className="form-field">
          <label className="lbl" htmlFor="hrk-para">Para Birimi <span className="req">*</span></label>
          <select id="hrk-para" className="form-select" value={para} onChange={(e) => setPara(e.target.value as typeof para)}>
            <option value="TRY">TRY — Türk Lirası</option>
            <option value="USD">USD — Dolar</option>
            <option value="EUR">EUR — Euro</option>
            <option value="GBP">GBP — Sterlin</option>
          </select>
        </div>
      </div>

      {para !== "TRY" ? (
        <div className="form-field">
          <label className="lbl" htmlFor="hrk-kur">Kur <span className="req">*</span></label>
          <input id="hrk-kur" className="form-input mono" inputMode="decimal" value={kur}
            onChange={(e) => setKur(e.target.value)} placeholder={`1 ${para} = ? TRY`} />
          <span className="help">Belge tarihindeki kur. Hesap {para} üzerinden yürür; kur yalnız TL karşılığı toplamı için.</span>
        </div>
      ) : null}

      <div className="cacc-drow">
        <div className="form-field">
          <label className="lbl" htmlFor="hrk-tarih">İşlem Tarihi <span className="req">*</span></label>
          <DateField id="hrk-tarih" value={tarih} onChange={setTarih} aria-label="İşlem tarihi" />
        </div>
        {odemeMi ? (
          <div className="form-field">
            <label className="lbl" htmlFor="hrk-yontem">Ödeme Yöntemi</label>
            <select id="hrk-yontem" className="form-select" value={yontem} onChange={(e) => setYontem(e.target.value as typeof yontem)}>
              <option value="transfer">Havale / EFT</option>
              <option value="cash">Nakit</option>
              <option value="check">Çek</option>
              <option value="promissory">Senet</option>
              <option value="credit_card">Kredi Kartı</option>
              <option value="offset">Mahsup / Takas</option>
            </select>
          </div>
        ) : null}
      </div>

      <div className="form-field">
        <label className="lbl" htmlFor="hrk-aciklama">Açıklama <span className="req">*</span></label>
        <input id="hrk-aciklama" className="form-input" value={aciklama} maxLength={300}
          onChange={(e) => setAciklama(e.target.value)} placeholder="Örn. 12.08 tarihli havale tahsilatı" />
      </div>

      <div className="cacc-drow">
        <div className="form-field">
          <label className="lbl" htmlFor="hrk-belge">Belge No</label>
          <input id="hrk-belge" className="form-input mono" value={belgeNo} maxLength={60}
            onChange={(e) => setBelgeNo(e.target.value)} placeholder="Dekont / makbuz no" />
        </div>
        <div className="form-field">
          <label className="lbl" htmlFor="hrk-ref">Referans</label>
          <input id="hrk-ref" className="form-input" value={referans} maxLength={120}
            onChange={(e) => setReferans(e.target.value)} placeholder="Çek/senet no, banka açıklaması" />
        </div>
      </div>
    </Drawer>
  );
}
